import moment from "moment";
import config from "../config";
import {
    noDataPlaceholder,
    dateFormat,
    changesReportType,
    CONVERGE_TYPE,
    LEAVE_CHANGES_SUBVIEW,
    leaveType,
    reportItems,
    logos,
    LEAVE_START_DATE_APPLY_OPTION,
    EXTENDED_LEAVE_START_DATE_SUBVIEWS,
    REPORTING_PERIOD_MAX_PAST_YEARS,
    REPORTING_PERIOD_MAX_FUTURE_YEARS,
    CUSTOM_DATE_RANGE,
    DATA_VIEW_TYPES,
    SERVICE_IDENTIFIERS,
} from "../constants";
import { REPORTING_PERIOD_DAYS_TO_ADD } from "unmsght-report-package";
import { setSelectedRelatedOrganizationsForReports } from "../slices/viewReportSlice";
import OrganizationRepository from "../repositories/OrganizationRepository";

export function createEnum(values) {
    const enumObject = {};

    for (const val of values) {
        enumObject[val] = val;
    }

    return Object.freeze(enumObject);
}

export function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function isNumeric(value) {
    return /^-?\d+$/.test(value);
}

export const isLessThanOne = (value) =>
    typeof value === "number" && value > 0 && value < 1;

export function unixTimestampToDate(timestamp) {
    const date = new Date(timestamp);
    return moment(date).format("MM/DD/YYYY hh:mm A");
}

export function dateToLocalTimestamp(timestamp) {
    const date = new Date(timestamp);
    return moment(date).local()._d;
}

export function normalizeDateOfBirth(dob) {
    if (!dob) return null;
    const value = String(dob).trim().split("T")[0];
    const parsed = moment(value, ["MM/DD/YYYY", "YYYY-MM-DD"], true);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : value;
}

export function formatHours(hour) {
    if (!hour || isNaN(hour)) return "";
    return parseFloat(hour).toFixed(2);
}

export function numberFormat(value) {
    if (value === null) return "$0.00";

    if (value !== null && isNaN(value)) return value;

    if (typeof value === "string") value = parseFloat(value);

    let formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    });

    return formatter.format(value);
}

export const sort = (rows, sortBy, asc = true) => {
    const rowsToSort = [...rows];

    return rowsToSort.sort((rowA, rowB) => {
        const aField = (rowA[sortBy] ?? "").toLowerCase();
        const bField = (rowB[sortBy] ?? "").toLowerCase();

        let comparison = 0;

        if (aField > bField) {
            comparison = 1;
        } else if (aField < bField) {
            comparison = -1;
        }

        return asc ? comparison : comparison * -1;
    });
};

export const removeLeadingZeros = (numString) => {
    const n = parseInt(numString);
    return n.toString();
};

export const formatDate = (date) => {
    const d = moment(date);

    if (d.isValid()) {
        return d.format(dateFormat);
    }

    return date;
};

const DATE_WORD_PATTERN = /\bdate\b/i;
const DATE_SUFFIX_PATTERN = /(Date|Dt)$/;
const DATE_SNAKE_SUFFIX_PATTERN = /(_date|_dt)$/i;
const DATE_CAMELCASE_PATTERN = /(date|dt)(?=$|[A-Z_])/i;
const LEADING_DATE_PREFIX_PATTERN =
    /^(\d{1,2}[/-]\d{1,2}[/-]\d{4}|\d{4}-\d{2}-\d{2})(?:[\sT].*)?$/;
const DATE_EXPORT_IDENTIFIER_EXCEPTIONS = new Set(["lastDayWorked"]);
const EXCEL_TEMPORAL_OVERRIDES = {
    "absence start time": "datetime",
    "absence end time": "datetime",
};
const EXCEL_TEMPORAL_IDENTIFIER_OVERRIDES = {
    startTime: "datetime",
    endTime: "datetime",
    fitForDutyRelease: "datetime",
};
const DATE_BASE_INPUT_FORMATS = [
    { base: "MM/DD/YYYY", meridiem: "hh:mm", military: "HH:mm" },
    { base: "M/D/YYYY", meridiem: "h:mm", military: "H:mm" },
    { base: "MM-DD-YYYY", meridiem: "hh:mm", military: "HH:mm" },
    { base: "M-D-YYYY", meridiem: "h:mm", military: "H:mm" },
];
const DATE_ONLY_INPUT_FORMATS = [
    ...DATE_BASE_INPUT_FORMATS.map(({ base }) => base),
    "YYYY-MM-DD",
];
const DATE_EXPORT_INPUT_FORMATS = [
    ...DATE_BASE_INPUT_FORMATS.flatMap(({ base, meridiem, military }) => [
        base,
        `${base} ${meridiem} A`,
        `${base} ${meridiem}:ss A`,
        `${base} ${military}:ss`,
        `${base} ${military}:ss.SSSSSS`,
    ]),
    moment.ISO_8601,
];
const DATETIME_EXPORT_INPUT_FORMATS = [
    ...DATE_BASE_INPUT_FORMATS.map(
        ({ base, meridiem }) => `${base} ${meridiem}:ss A`
    ),
    moment.ISO_8601,
];
export const EXCEL_DATE_ONLY_FORMAT = "mm/dd/yyyy";
export const EXCEL_DATE_TIME_FORMAT = "mm/dd/yyyy hh:mm:ss AM/PM";

const normalizeExcelExportText = (value) =>
    typeof value === "string"
        ? value.trim().replace(/\s+/g, " ").toLowerCase()
        : "";

const cloneExcelExportDate = (value, includeTime = false) =>
    includeTime
        ? new Date(
              value.getFullYear(),
              value.getMonth(),
              value.getDate(),
              value.getHours(),
              value.getMinutes(),
              value.getSeconds(),
              value.getMilliseconds()
          )
        : new Date(value.getFullYear(), value.getMonth(), value.getDate());

const getTrimmedExcelExportValue = (value) => {
    if (typeof value !== "string") {
        return null;
    }

    const trimmedValue = value.trim();
    return trimmedValue && trimmedValue.toLowerCase() !== "no access"
        ? trimmedValue
        : null;
};

const hasDateExportIdentifier = (column = {}) => {
    const identifiers = [column?.accessorKey, column?.id].filter(Boolean);
    return identifiers.some((identifier) => {
        if (typeof identifier !== "string") {
            return false;
        }

        const trimmedIdentifier = identifier.trim();
        if (!trimmedIdentifier) {
            return false;
        }

        if (DATE_EXPORT_IDENTIFIER_EXCEPTIONS.has(trimmedIdentifier)) {
            return true;
        }

        return (
            DATE_WORD_PATTERN.test(trimmedIdentifier) ||
            DATE_SUFFIX_PATTERN.test(trimmedIdentifier) ||
            DATE_SNAKE_SUFFIX_PATTERN.test(trimmedIdentifier) ||
            DATE_CAMELCASE_PATTERN.test(trimmedIdentifier)
        );
    });
};

export const getExcelExportTemporalType = (column = {}) => {
    const normalizedHeader = normalizeExcelExportText(column?.header);
    const temporalIdentifierOverride = [column?.accessorKey, column?.id]
        .map((identifier) => EXCEL_TEMPORAL_IDENTIFIER_OVERRIDES[identifier])
        .find(Boolean);

    return (
        EXCEL_TEMPORAL_OVERRIDES[normalizedHeader] ||
        temporalIdentifierOverride ||
        (hasDateExportIdentifier(column) ? "date" : null)
    );
};

export const isDateExportColumn = (column = {}) => {
    return hasDateExportIdentifier(column);
};

export const parseExcelExportDate = (value) => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return cloneExcelExportDate(value);
    }

    const trimmedValue = getTrimmedExcelExportValue(value);
    if (!trimmedValue) {
        return null;
    }

    const parsedDate = moment(trimmedValue, DATE_EXPORT_INPUT_FORMATS, true);
    if (!parsedDate.isValid()) {
        const leadingDateMatch = LEADING_DATE_PREFIX_PATTERN.exec(trimmedValue);
        if (leadingDateMatch) {
            const dateOnly = moment(
                leadingDateMatch[1],
                DATE_ONLY_INPUT_FORMATS,
                true
            );
            if (dateOnly.isValid()) {
                return dateOnly.startOf("day").toDate();
            }
        }
        return null;
    }

    return parsedDate.startOf("day").toDate();
};

export const parseExcelExportDateTime = (value) => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return cloneExcelExportDate(value, true);
    }

    const trimmedValue = getTrimmedExcelExportValue(value);
    if (!trimmedValue) {
        return null;
    }

    const parsedDateTime = moment(
        trimmedValue,
        DATETIME_EXPORT_INPUT_FORMATS,
        true
    );
    if (parsedDateTime.isValid()) {
        return parsedDateTime.toDate();
    }

    return parseExcelExportDate(trimmedValue);
};

export const resolveExcelExportTemporalValue = (value, temporalType) => {
    if (!temporalType) {
        return null;
    }

    if (temporalType === "datetime") {
        const parsedValue = parseExcelExportDateTime(value);
        return parsedValue ? { parsedValue, resolvedType: "datetime" } : null;
    }

    const parsedValue = parseExcelExportDate(value);
    return parsedValue ? { parsedValue, resolvedType: "date" } : null;
};

export const toExcelDateSerial = (date) =>
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000 +
    25569;

export const toExcelDateTimeSerial = (date) =>
    Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
    ) /
        86400000 +
    25569;

export const toExcelTemporalSerial = (date) => toExcelDateTimeSerial(date);

export const getExcelExportNumberFormat = (temporalType) =>
    temporalType === "datetime"
        ? EXCEL_DATE_TIME_FORMAT
        : EXCEL_DATE_ONLY_FORMAT;

export const checkNullValue = (dataProperty, placeHolder) => {
    if (dataProperty != null && dataProperty != "") return dataProperty;
    else return placeHolder;
};

export const formattedDate = (dateobject, _noDataPlaceholder) => {
    return dateobject
        ? moment(dateobject).format("MM/DD/YYYY")
        : _noDataPlaceholder;
};

export const checkNull = (dateobject, _noDataPlaceholder) => {
    return dateobject ? dateobject : _noDataPlaceholder;
};

export const checkWhiteSpace = (dateobject, noDataPlaceholder) => {
    return dateobject === " " || null ? noDataPlaceholder : dateobject;
};

const IN_ERROR_REASON_REGEX = /(?:\bmuerr\b|\bmuper\b|error)/i;

export const isInErrorReason = (value) => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) {
        return value.some(isInErrorReason);
    }
    const text = String(value).trim();
    if (!text) return false;
    return IN_ERROR_REASON_REGEX.test(text);
};

export const hasInErrorReason = (...values) =>
    values.some((value) => isInErrorReason(value));

export const getLogoutRedirectUrl = (user, returnUrl) => {
    if (user?.isUserInternal) {
        // Internal user login page redirect URL
        return config.REACT_APP_ISERVICES + ":81/LanAuthentication";
    }

    if (returnUrl) return returnUrl;

    // Default Login Page - iServices
    return config.REACT_APP_ISERVICES;
};

export const separateByDelimiter = (arr, delimiter) => {
    if (arr != null) {
        let a = arr.split(delimiter);
        return a.join(" " + delimiter + " ");
    } else return "";
};

export const formatClaimDate = (d) => {
    return d ? moment(d).format(dateFormat) : noDataPlaceholder;
};

export const filterDuplicatesFromArray = (arr) => {
    return arr.filter((value, index, self) => {
        return self.indexOf(value) === index;
    });
};

export const sortedRelatedOrgsArray = (arr) => {
    const options = [];
    arr?.forEach((item) => {
        const option = {
            value: item.ORGANIZATIONID,
            label: item.ORGNIZATIONNAME,
        };

        options.push(option);
    });

    return [...options].sort((a, b) => a.label.localeCompare(b.label));
};

export const getOrganizationLogo = (organizationName) => {
    let show = false;
    let logo = null;

    if (typeof organizationName === "string") {
        const name = organizationName.toLowerCase();

        // Costco
        if (name.includes("costco")) {
            show = true;
            logo = logos.costco;
        }
    }

    return {
        show,
        logo,
    };
};

export const getReportBreadcrumbLinks = (org, emp) => {
    const links = [
        {
            path: "/search",
            name: "Search",
        },
        {
            path: "/reports",
            name: "Reports",
        },
    ];

    if (org !== null) {
        const orgId = org.ORGANIZATIONID;

        const organizationLink = {
            path: `/organization/${orgId}/employees/search`,
            name: "Employee",
        };

        links.splice(1, 0, organizationLink);

        if (emp !== null && emp.path && emp.organizationId == orgId) {
            const employeeLink = {
                path: emp.path,
                name: "Claim & Leave",
            };

            links.splice(2, 0, employeeLink);
        }
    }

    return links;
};

export const getApiUrl = () => {
    const persistRoot = JSON.parse(localStorage.getItem("persist:root"));

    if (persistRoot) {
        const authData = JSON.parse(persistRoot.auth);
        const isInternal = authData.user?.isUserInternal;

        return isInternal
            ? config.REACT_APP_API_URL
            : config.REACT_APP_EXTERNAL_PREFERENCE_API_URL;
    }
};

export const getDisclaimerDate = () => {
    const today = new Date();
    const month = today.getMonth() + 1;
    const year = today.getFullYear();
    const date = today.getDate();
    const hour = today.getHours();
    const minute = (today.getMinutes() < 10 ? "0" : "") + today.getMinutes();
    const time = timeConversion(hour, minute);
    return ` ${month}/${date}/${year}  ${time}`;
};

export const timeConversion = (hour, minute) => {
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    hour = hour ? hour : 12;
    return `${hour}:${minute} ${ampm}`;
};

export const getPreviousDays = (date, day) => {
    return moment(date).subtract(day, "days").format("YYYY-MM-DD");
};

export const getStandardDateFormat = (date, day) => {
    return moment(date).subtract(day, "days").format("MM/DD/YYYY");
};

export const getPreviousDaysStart = (date, duration) => {
    return moment(date)
        .subtract(1, duration)
        .startOf(duration)
        .format("YYYY-MM-DD");
};
export const getAccessoryKey = (reportViewType, accessorKey) => {
    if (reportViewType === changesReportType && accessorKey === "fullName") {
        return "name";
    } else {
        return accessorKey;
    }
};

export const getPreviousDaysEnd = (date, duration) => {
    return moment(date)
        .subtract(1, duration)
        .endOf(duration)
        .format("YYYY-MM-DD");
};

export const getPresentDayFormat = (date) => {
    return moment(date).format("YYYY-MM-DD");
};

const isExcludedReportingPeriodSubview = (subviewName) =>
    typeof subviewName === "string" &&
    (subviewName.includes("All Open") || subviewName === "Leave Changes");

const formatYmd = (dateValue) => {
    if (
        typeof dateValue === "string" &&
        /^\d{4}-\d{2}-\d{2}$/.test(dateValue)
    ) {
        return dateValue;
    }

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) {
        return dateValue;
    }

    return getPresentDayFormat(parsedDate);
};

const addDaysToYmd = (ymd, days) => {
    const parsedDate = moment(ymd, "YYYY-MM-DD", true);
    if (!parsedDate.isValid()) {
        return ymd;
    }

    return parsedDate.add(days, "days").format("YYYY-MM-DD");
};

export const shouldAdjustReportingPeriodToDate = ({
    effectiveEndDate,
    subviewName,
}) => {
    if (!effectiveEndDate || isExcludedReportingPeriodSubview(subviewName)) {
        return false;
    }

    return formatYmd(effectiveEndDate) === formatYmd(new Date());
};

export const getAdjustedEffectiveEndDateForApi = ({
    effectiveEndDate,
    subviewName,
}) => {
    if (!shouldAdjustReportingPeriodToDate({ effectiveEndDate, subviewName })) {
        return effectiveEndDate;
    }

    return addDaysToYmd(
        formatYmd(effectiveEndDate),
        REPORTING_PERIOD_DAYS_TO_ADD
    );
};

const isAppliedToLeaveStartDate = (applyingReportingPeriod) =>
    Array.isArray(applyingReportingPeriod) &&
    applyingReportingPeriod.some(
        (option) => option?.name === LEAVE_START_DATE_APPLY_OPTION
    );

// Extends Reporting Period bounds only when Apply Reporting Period To =
// "Leave Start Date" on Leave Summary, Leave Period Protection Detail, or
// Leave Details: From caps at today (no future), To extends 1 year ahead;
// both share the 5-year past floor. Falls back to backend-provided bounds.
export const getReportingPeriodDateBounds = ({
    subviewName,
    applyingReportingPeriod,
    minimumStartDate,
    maximumEndDate,
}) => {
    const isEligibleSubview =
        EXTENDED_LEAVE_START_DATE_SUBVIEWS.includes(subviewName);

    if (
        !isEligibleSubview ||
        !isAppliedToLeaveStartDate(applyingReportingPeriod)
    ) {
        return { minimumStartDate, maximumEndDate };
    }

    const today = getPresentDayFormat(moment());

    return {
        minimumStartDate: getPresentDayFormat(
            moment().subtract(REPORTING_PERIOD_MAX_PAST_YEARS, "years")
        ),
        maximumStartDate: today,
        maximumEndDate: getPresentDayFormat(
            moment().add(REPORTING_PERIOD_MAX_FUTURE_YEARS, "years")
        ),
    };
};

export const capReportingPeriodEndToToday = (reportingPeriod) => {
    const endStr = reportingPeriod?.value?.effectiveEndDt;
    const endMoment = moment(endStr, dateFormat, true);
    if (!endMoment.isValid() || !endMoment.isAfter(moment().startOf("day"))) {
        return reportingPeriod;
    }

    const todayFormatted = moment().format(dateFormat);
    return {
        ...reportingPeriod,
        displayName: `${CUSTOM_DATE_RANGE} ${reportingPeriod.value.effectiveStartDt} - ${todayFormatted}`,
        value: {
            ...reportingPeriod.value,
            effectiveEndDt: todayFormatted,
        },
    };
};

export const getDateTimeFormate = (date) => {
    return moment(date).format("YYYY-MM-DD") + "T00:00:00Z";
};

export const getPreviousDaysWithTimeFormat = (date, day) => {
    return (
        moment(date).subtract(day, "days").format("YYYY-MM-DD") + "T00:00:00Z"
    );
};

export const isCostcoUser = (userSelectedOrganizations, id) => {
    const organizationName =
        userSelectedOrganizations[
            id
        ]?.related[0]?.ORGANIZATIONGROUPNAME?.toUpperCase();
    return organizationName?.includes("COSTCO");
};

export function getSelectedIsfOrganizationIds(
    userSelectedOrganizationReports,
    reportId,
    selectedRelatedOrganizations
) {
    let selectedIsfOrganizationIds = [];
    let organizationGroupId = null;
    let relatedOrganizationNames = null;
    let organizationName = null;
    let selectedRelatedOrgs = [];

    // Scope by the user's related-org selection whenever the selected org has related orgs -
    // applies to single-parent (uniform group) users too, not just multi-parent.
    if (userSelectedOrganizationReports[reportId]?.related.length > 0) {
        organizationName =
            userSelectedOrganizationReports[reportId]?.organization
                .ORGNIZATIONNAME;
        // Check if selectedRelatedOrganizations is an array before calling map
        if (Array.isArray(selectedRelatedOrganizations)) {
            selectedIsfOrganizationIds = selectedRelatedOrganizations.map(
                (org) => org.organizationIDISF
            );
            let labels = selectedRelatedOrganizations.map((item) => item.label);
            relatedOrganizationNames = labels.join(", ");
            organizationGroupId =
                selectedRelatedOrganizations[0]?.organizationGroupId;
            organizationName =
                userSelectedOrganizationReports[reportId].organization
                    .ORGANIZATIONGROUPNAME;
            selectedRelatedOrgs = selectedRelatedOrganizations.map((org) => ({
                OrganizationId: org.organizationIDISF,
                OrganizationName: org.label,
            }));
        }
    } else {
        selectedIsfOrganizationIds = [
            userSelectedOrganizationReports[reportId]?.organization
                ?.ORGANIZATIONIDISF,
        ];
        organizationName =
            userSelectedOrganizationReports[reportId]?.organization
                ?.ORGNIZATIONNAME;
        selectedRelatedOrgs = [];
    }

    return {
        selectedIsfOrganizationIds,
        organizationGroupId,
        relatedOrganizationNames,
        organizationName,
        selectedRelatedOrgs,
    };
}
export const getButtonDisabledStatus = (
    viewNameChange,
    viewNameValue,
    descriptionChange,
    descriptionValue
) => {
    const isViewNameValid =
        viewNameChange &&
        viewNameValue?.length >= 4 &&
        viewNameValue?.length <= 50;
    const isDescriptionValid =
        descriptionChange &&
        descriptionValue?.length >= 4 &&
        descriptionValue?.length <= 100;
    return !(isViewNameValid && isDescriptionValid);
};
export const isParameterEnabled = (reportParametersOptions, paramName) =>
    reportParametersOptions?.some(
        (param) => param.parameterName === paramName && param.enabled
    );

export const matchReportUrl = (path) => {
    const cleanedPath = path.replace(/^\/+/, "");
    return /^reportingcapabilities\/(Absence|Leave|Disability|Life|PaymentHistory|Changes|Documents)$/i.test(
        cleanedPath
    );
};

export const getPageReferrerTitle = (page_referrer_url, user, dispatch) => {
    const employeePage = /organization\/[^\/]+\/employees\/search/s;
    const claimLeavePage = /organization\/[^\/]+\/employees\/[^\/]+/gm;

    const clearReportsSelection = () =>
        dispatch(setSelectedRelatedOrganizationsForReports([]));

    if (user.isUserInternal && page_referrer_url.includes("/login")) {
        return "internal";
    } else if (
        page_referrer_url.match(employeePage) &&
        !page_referrer_url.includes("/login")
    ) {
        return "employee search";
    } else if (page_referrer_url.match(claimLeavePage)) {
        return "claim & leave";
    } else if (page_referrer_url.includes("/home")) {
        return "home";
    } else if (page_referrer_url.includes("/help")) {
        clearReportsSelection();
        return "help";
    } else if (page_referrer_url.includes("/preferences")) {
        return "preferences";
    } else if (page_referrer_url?.includes("/dashboard")) {
        return "dashboard";
    } else if (
        (page_referrer_url.includes("/login") &&
            page_referrer_url.includes("uaservices")) ||
        page_referrer_url.includes("services")
    ) {
        return "iservices";
    } else {
        return "search";
    }
};
// Dynamic comparisons based on selected dropdown value

export const getComparisons = (organizationName) => ({
    "Industry Peers": [
        {
            key: "Org",
            name: organizationName,
            color: "#037CB7",
            borderColor: "#037CB7",
        },
        {
            key: "Industrypeers",
            name: "Industry peers",
            color: "#E7F3F4",
            borderColor: "#0199A6",
        },
    ],
    "Unum Clients": [
        {
            key: "Org",
            name: organizationName,
            color: "#037CB7",
            borderColor: "#037CB7",
        },
        {
            key: "unumclient",
            name: "Unum clients",
            color: "#4DB8C1",
            borderColor: "#4DB8C1",
        },
    ],
    All: [
        {
            key: "Org",
            name: organizationName,
            color: "#037CB7",
            borderColor: "#037CB7",
        },
        {
            key: "Industrypeers",
            name: "Industry peers",
            color: "#E7F3F4",
            borderColor: "#0199A6",
        },
        {
            key: "unumclient",
            name: "Unum clients",
            color: "#4DB8C1",
            borderColor: "#016B74",
        },
    ],
    "Organization Only": [
        {
            key: "orgValue",
            name: "Organization Only",
            color: "#E7F3F4",
            borderColor: "#0199A6",
        },
    ],
});

export const getPieComparisons = (organizationName) => ({
    industryPeers: [
        {
            key: "Org",
            name: organizationName,
            pieColors: ["#037CB7", "#E6F2F8", "#9ACBE2"],
        },
        {
            key: "IndustryPeers",
            name: "Industry peers",
            pieColors: ["#037CB7", "#E6F2F8", "#9ACBE2"],
        },
    ],
    unumBlock: [
        {
            key: "Org",
            name: organizationName,
            pieColors: ["#037CB7", "#E6F2F8", "#9ACBE2"],
        },
        {
            key: "unumclient",
            name: "Unum Client",
            pieColors: ["#037CB7", "#E6F2F8", "#9ACBE2"],
        },
    ],
    All: [
        {
            key: "Org",
            name: organizationName,
            pieColors: ["#037CB7", "#E6F2F8", "#9ACBE2"],
        },
        {
            key: "IndustryPeers",
            name: "Industry peers",
            pieColors: ["#037CB7", "#E6F2F8", "#9ACBE2"],
        },
        {
            key: "unumclient",
            name: "Unum Client",
            pieColors: ["#037CB7", "#E6F2F8", "#9ACBE2"],
        },
    ],
    "Organization Only": [
        {
            key: "Org",
            name: "Organization Only",
            pieColors: [
                "#025780",
                "#E6EEF4",
                "#4FA3CD",
                "#8CC442",
                "#0199A6",
                "#FAC832",
            ],
            borderColor: [
                "#025780",
                "#015294",
                "#4FA3CD",
                "#62892E",
                "#016B74",
                "#AF8C23",
            ],
        },
    ],
});

export function getNavItems(user, config, returnUrl) {
    const navItems = [
        { id: "Search", path: "/search", label: "Search", condition: true },
        {
            id: "reports",
            path: "/reportingcapabilities",
            label: "Reports",
            condition: true,
        },
        {
            id: "preferences",
            path: "/preferences",
            label: "Preferences",
            condition: true,
        },
        {
            id: "iservices",
            path: null,
            label: "iServices",
            condition: user?.isUserInternal,
            onClick: () =>
                window.open(
                    config.REACT_APP_ISERVICES + ":81/LanAuthentication",
                    "_blank"
                ),
        },
        {
            id: "iservices-external",
            path: null,
            label: "iServices",
            condition:
                returnUrl !== null &&
                !user?.isUserInternal &&
                !returnUrl?.includes("my.unum.com"),
            onClick: () => {
                let redirectUrl = returnUrl;
                if (redirectUrl.includes("services.unum.com")) {
                    redirectUrl =
                        config.REACT_APP_ISERVICES + "/_fwWelc/Welcome.aspx";
                }
                return window.open(redirectUrl, "_blank");
            },
        },
        {
            id: "CLI",
            path: null,
            label: "CLI",
            condition: true,
            onClick: () =>
                window.open(
                    user?.isUserInternal
                        ? config.REACT_APP_CLI_INTERNAL
                        : config.REACT_APP_CLI,
                    "_blank"
                ),
        },
        {
            id: "MyUnum",
            path: null,
            label: "MyUnum For Clients",
            condition: returnUrl && returnUrl.includes("my.unum.com"),
            onClick: () =>
                window.location.replace(config.REACT_APP_MY_UNUM, "_blank"),
        },
    ];

    return navItems;
}

export const generateCorrelationId = () => {
    if (typeof crypto !== "undefined") {
        const guid = crypto.randomUUID();
        return guid;
    }
    return null;
};

export const handleAuthError = (error, redirectFunction = null) => {
    const isAuthError =
        error?.status === 401 ||
        error?.originalStatus === 401 ||
        error?.data?.status === 401 ||
        (error?.error &&
            (error.error.status === 401 || error.error.originalStatus === 401));

    if (isAuthError) {
        console.warn(
            "Authentication error detected (401). Redirecting to login..."
        );

        if (redirectFunction && typeof redirectFunction === "function") {
            redirectFunction();
        } else {
            window.location.href = config.REACT_APP_ISERVICES || "/login";
        }

        return true;
    }

    return false;
};

export const createBaseQueryWithAuthHandling = (
    baseQuery,
    redirectFunction = null
) => {
    return async (args, api, extraOptions) => {
        const result = await baseQuery(args, api, extraOptions);

        if (result.error) {
            const authErrorHandled = handleAuthError(
                result.error,
                redirectFunction
            );
            if (authErrorHandled) {
                return {
                    ...result,
                    error: {
                        ...result.error,
                        authErrorHandled: true,
                    },
                };
            }
        }

        return result;
    };
};

export function getRelatedOrgNames(input) {
    let records;
    if (
        input &&
        input.organization !== undefined &&
        input.related !== undefined
    ) {
        records = [input];
    } else if (input && typeof input === "object" && !Array.isArray(input)) {
        records = Object.values(input);
    } else {
        records = [];
    }

    const names = [];
    records.forEach(({ related }) => {
        if (Array.isArray(related) && related.length > 0) {
            related.forEach((o) => {
                if (o?.ORGNIZATIONNAME) {
                    names.push(o.ORGNIZATIONNAME.toLowerCase());
                }
            });
        }
    });

    const joined = names.join(",");
    return joined.length ? joined : undefined;
}

export const getOrganizationGroupId = (selectedReports, reportId) => {
    if (!selectedReports || !reportId) return null;
    const node = selectedReports[reportId];
    if (!node) return undefined;
    return (
        node.organization?.ORGANIZATIONGROUPID ??
        node.related?.[0]?.ORGANIZATIONGROUPID ??
        undefined
    );
};

export function buildGtmOrgContext(selectedMap, key) {
    const node = selectedMap?.[key];

    const employerName = (
        node?.organization?.ORGANIZATIONGROUPNAME ??
        node?.organization?.ORGNIZATIONNAME ??
        ""
    ).toLowerCase();

    const relatedOrgNames = getRelatedOrgNames(node);
    const organizationGroupId = getOrganizationGroupId(selectedMap, key);

    return { employerName, relatedOrgNames, organizationGroupId };
}

export function ensureSelectedMapEntry(
    id,
    selectedMap = {},
    allOrganizations = []
) {
    if (!id) return selectedMap || {};
    if (selectedMap?.[id]?.organization) return selectedMap;

    const group =
        OrganizationRepository.getRelatedOrganizationsFromIds(
            allOrganizations,
            [id]
        ) || [];

    const primary =
        group.find((o) => String(o.ORGANIZATIONID) === String(id)) || group[0];

    if (!primary) return selectedMap || {};

    return {
        ...selectedMap,
        [id]: {
            organization: primary,
            related: primary?.ORGANIZATIONGROUPID ? group : [],
        },
    };
}

export const getInsightClassName = (myInsights, selectedOrganization) => {
    if (myInsights && selectedOrganization) {
        return "col-lg-10 col-md-10 col-sm-6";
    }
    if (selectedOrganization) {
        return "col-lg-10 col-md-10 col-sm-6 md-mb-20p pl-0p";
    }
    if (myInsights && !selectedOrganization) {
        return "col-lg-6 col-md-6 col-sm-6 md-mb-20p";
    }
    return "col-lg-4 col-md-4 col-sm-6 md-mb-20p";
};

const toLowerCase = (value) =>
    typeof value === "string" ? value.toLowerCase() : value;

export function getIsNewLeaveChangesEnabled({
    displayNewLeaveChangesReport,
    user,
    allOrganizations,
}) {
    return isUserEnabledForFlag(
        displayNewLeaveChangesReport,
        user,
        allOrganizations
    );
}

export function getIsDocumentLibraryTileEnabled({
    displayDocumentLibraryTile,
    user,
    allOrganizations,
}) {
    return isUserEnabledForFlag(
        displayDocumentLibraryTile,
        user,
        allOrganizations
    );
}

export function getIsOldChangesTileEnabled({
    displayoldChangesTile,
    user,
    allOrganizations,
}) {
    return isUserEnabledForFlag(displayoldChangesTile, user, allOrganizations);
}

// Evaluates LD flags shaped as { enableExternalUser, enableInternalUser, enableOrganizationList, isfUserIds }
export function isUserEnabledForFlag(flagValue, user, allOrganizations) {
    if (!flagValue || typeof flagValue !== "object") return false;

    const config =
        typeof flagValue.default === "object" && flagValue.default !== null
            ? flagValue.default
            : flagValue;

    const {
        enableExternalUser = false,
        enableInternalUser = false,
        enableOrganizationList,
        isfUserIds = [],
    } = config;

    const isUserInIsfList = Array.isArray(isfUserIds)
        ? isfUserIds.map(toLowerCase).includes(toLowerCase(user?.userId))
        : isfUserIds
              ?.split(",")
              .map(toLowerCase)
              .includes(toLowerCase(user?.userId));

    if (isUserInIsfList) return true;

    if (enableInternalUser && user?.isUserInternal) return true;

    if (enableExternalUser && !user?.isUserInternal) return true;

    if (enableOrganizationList && Array.isArray(enableOrganizationList)) {
        const allOrgIds = new Set(
            allOrganizations?.map((org) => org.ORGANIZATIONIDISF) || []
        );
        if (enableOrganizationList.some((org) => allOrgIds.has(org)))
            return true;
    }

    return false;
}

export function getIsDashboardEnabled({
    displayDisabilitySnapshotTile,
    displayTrendsBenchmarkingTile,
    displayCostcoDashboard,
    user,
    enabledOrganizationsDisabilitySnapshot,
    enabledOrganizationsTrendsBenchmarking,
    allOrganizations,
}) {
    const {
        enableExternalUser: disabilitySnapshotExternalEnabled,
        enableInternalUser: disabilitySnapshotInternalEnabled,
        enableOrganizationList: disabilitySnapshotOrganizationListEnabled,
        isfUserIds: disabilitySnapshotIsfUserIdsEnabled = [],
    } = displayDisabilitySnapshotTile || {};

    const {
        enableExternalUser: trendsBenchmarkingExternalEnabled,
        enableInternalUser: trendsBenchmarkingInternalEnabled,
        enableOrganizationList: trendsBenchmarkingOrganizationListEnabled,
        isfUserIds: trendsBenchmarkingIsfUserIdsEnabled = [],
    } = displayTrendsBenchmarkingTile || {};

    const { isfUserIds: costcoDashboardIsfUserIdsEnabled = [] } =
        displayCostcoDashboard || {};

    const enabledDashboardUsers = [
        ...new Set(
            [
                ...disabilitySnapshotIsfUserIdsEnabled,
                ...trendsBenchmarkingIsfUserIdsEnabled,
                ...costcoDashboardIsfUserIdsEnabled,
            ].map((id) => id?.toLowerCase())
        ),
    ];

    const allOrgIds = new Set(
        allOrganizations?.map((org) => org.ORGANIZATIONIDISF) || []
    );

    const enabledDashboardFromOrgList =
        (disabilitySnapshotOrganizationListEnabled &&
            enabledOrganizationsDisabilitySnapshot?.some((org) =>
                allOrgIds.has(org)
            )) ||
        (trendsBenchmarkingOrganizationListEnabled &&
            enabledOrganizationsTrendsBenchmarking?.some((org) =>
                allOrgIds.has(org)
            ));

    const enabledDashboardInternal =
        (disabilitySnapshotInternalEnabled && user?.isUserInternal) ||
        (trendsBenchmarkingInternalEnabled && user?.isUserInternal);

    const enabledDashboardExternal =
        (disabilitySnapshotExternalEnabled && !user?.isUserInternal) ||
        (trendsBenchmarkingExternalEnabled && !user?.isUserInternal);

    return (
        enabledDashboardFromOrgList ||
        enabledDashboardUsers.includes(user?.userId?.toLowerCase()) ||
        enabledDashboardInternal ||
        enabledDashboardExternal
    );
}

export function getEnabledDashboardTiles({
    user,
    flags,
    enabledOrganizationsDisabilitySnapshot,
    enabledOrganizationsTrendsBenchmarking,
    allOrganizations,
}) {
    const {
        displayDisabilitySnapshotTile,
        displayTrendsBenchmarkingTile,
        displayCostcoDashboard,
    } = flags;

    const isDisabilitySnapshotEnabled = () => {
        if (!displayDisabilitySnapshotTile) return false;

        const {
            enableExternalUser,
            enableInternalUser,
            enableOrganizationList,
            isfUserIds = [],
        } = displayDisabilitySnapshotTile;

        const isUserInIsfList = Array.isArray(isfUserIds)
            ? isfUserIds.map(toLowerCase).includes(toLowerCase(user?.userId))
            : isfUserIds
                  .split(",")
                  .map(toLowerCase)
                  .includes(toLowerCase(user?.userId));

        const allOrgIds = new Set(
            allOrganizations?.map((org) => org.ORGANIZATIONIDISF) || []
        );

        const isOrgEnabled =
            enableOrganizationList &&
            enabledOrganizationsDisabilitySnapshot?.some((org) =>
                allOrgIds.has(org)
            );

        return (
            isUserInIsfList ||
            (enableInternalUser && user?.isUserInternal) ||
            (enableExternalUser && !user?.isUserInternal) ||
            isOrgEnabled
        );
    };

    const isTrendsBenchmarkingEnabled = () => {
        if (!displayTrendsBenchmarkingTile) return false;

        const {
            enableExternalUser,
            enableInternalUser,
            enableOrganizationList,
            isfUserIds = [],
        } = displayTrendsBenchmarkingTile;

        const isUserInIsfList = Array.isArray(isfUserIds)
            ? isfUserIds.map(toLowerCase).includes(toLowerCase(user?.userId))
            : isfUserIds
                  .split(",")
                  .map(toLowerCase)
                  .includes(toLowerCase(user?.userId));

        const allOrgIds = new Set(
            allOrganizations?.map((org) => org.ORGANIZATIONIDISF) || []
        );

        const isOrgEnabled =
            enableOrganizationList &&
            enabledOrganizationsTrendsBenchmarking?.some((org) =>
                allOrgIds.has(org)
            );

        return (
            isUserInIsfList ||
            (enableInternalUser && user?.isUserInternal) ||
            (enableExternalUser && !user?.isUserInternal) ||
            isOrgEnabled
        );
    };

    const isCostcoDashboardEnabled = () => {
        if (!displayCostcoDashboard) return false;

        const { isfUserIds = [] } = displayCostcoDashboard;

        const isUserInIsfList = Array.isArray(isfUserIds)
            ? isfUserIds.map(toLowerCase).includes(toLowerCase(user?.userId))
            : isfUserIds
                  .split(",")
                  .map(toLowerCase)
                  .includes(toLowerCase(user?.userId));

        return isUserInIsfList;
    };

    return reportItems.filter((item) => {
        if (item.path === "disability-snapshot")
            return isDisabilitySnapshotEnabled();
        if (item.path === "trendsAndBenchmarking")
            return isTrendsBenchmarkingEnabled();
        if (item.path === "costco-analytics-dashboards")
            return isCostcoDashboardEnabled();
        return false;
    });
}

export function getAllowedPoliciesAndReportingGroups(
    storedPortalUserData,
    selectedIsfOrganizationIds,
    dataViewType = ""
) {
    let allowedPoliciesAndDivisions = [];
    let allowedReportingGroups = [];
    let unsortedAllowedReportingGroups = [];
    const selectedOrgIds = new Set(
        (selectedIsfOrganizationIds || [])
            .filter(Boolean)
            .map((orgId) => String(orgId).trim().toLowerCase())
    );

    if (
        storedPortalUserData &&
        storedPortalUserData.assignedOrgs &&
        Array.isArray(storedPortalUserData.assignedOrgs)
    ) {
        const fullListOfPoliciesAndDivisions = {};
        storedPortalUserData.assignedOrgs.forEach((assignedOrg) => {
            if (selectedOrgIds.has(assignedOrg.orgId?.trim().toLowerCase())) {
                assignedOrg.assignedPolicies.forEach((policy) => {
                    const {
                        policyId,
                        divisionId,
                        divisionName,
                        reportingGroupInd,
                        serviceIdentifiers = [],
                    } = policy;

                    if (
                        (DATA_VIEW_TYPES.DOCUMENTS === dataViewType &&
                            !serviceIdentifiers.includes(
                                SERVICE_IDENTIFIERS.TAX_REPORTING
                            )) ||
                        (DATA_VIEW_TYPES.DOCUMENTS !== dataViewType &&
                            !serviceIdentifiers.includes(
                                SERVICE_IDENTIFIERS.LEAVE_REPORTING
                            ))
                    ) {
                        return;
                    }

                    if (reportingGroupInd === "N") {
                        if (!fullListOfPoliciesAndDivisions[policyId]) {
                            fullListOfPoliciesAndDivisions[policyId] = {
                                policyId,
                                divisions: [],
                            };
                        }
                        fullListOfPoliciesAndDivisions[policyId].divisions.push(
                            { divisionId, divisionName }
                        );
                    }

                    if (reportingGroupInd === "Y") {
                        unsortedAllowedReportingGroups.push({
                            value: policyId + "." + divisionId,
                            name: String(+divisionId) + " - " + divisionName,
                            divisionId: +divisionId,
                            divisionName: divisionName,
                        });
                    }
                });
            }
        });

        allowedPoliciesAndDivisions = Object.values(
            fullListOfPoliciesAndDivisions
        );

        unsortedAllowedReportingGroups.sort((a, b) => {
            if (a.divisionId !== b.divisionId) {
                return a.divisionId - b.divisionId;
            }
            if (a.divisionName === undefined || b.divisionName === undefined) {
                return 0;
            }
            return a.divisionName.localeCompare(b.divisionName);
        });

        allowedReportingGroups.push(...unsortedAllowedReportingGroups);
    }

    return { allowedPoliciesAndDivisions, allowedReportingGroups };
}

export const getDocumentLibraryPermission = (portalUserData) => {
    if (
        portalUserData &&
        portalUserData?.assignedOrgs &&
        Array.isArray(portalUserData.assignedOrgs)
    ) {
        for (const org of portalUserData.assignedOrgs) {
            if (org?.assignedPolicies && Array.isArray(org.assignedPolicies)) {
                for (const policy of org.assignedPolicies) {
                    if (
                        policy?.serviceIdentifiers &&
                        Array.isArray(policy.serviceIdentifiers) &&
                        policy.serviceIdentifiers.includes(
                            SERVICE_IDENTIFIERS.TAX_REPORTING
                        )
                    ) {
                        return true;
                    }
                }
            }
        }
    }

    return false;
};

export const RemoveYear = (dateobject) => {
    if (dateobject !== undefined || dateobject !== "") {
        if (dateobject?.includes("2099")) {
            return "—";
        } else {
            return dateobject;
        }
    } else {
        return null;
    }
};

export async function fetchImageAsBase64(url) {
    if (!url) return null;

    const response = await fetch(url);
    if (!response.ok)
        throw new Error(`Failed to fetch image: ${response.status}`);
    const blob = await response.blob();

    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64 = reader.result?.split(",")[1];
            base64
                ? resolve(base64)
                : reject(new Error("Failed to read image"));
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

export function getSubViewNames({
    enableExternalUser,
    enableInternalUser,
    enableOrganizationList,
    isfUserIds,
    subViewNames,
    user,
}) {
    const isUserMatched = isfUserIds
        .map(toLowerCase)
        .includes(toLowerCase(user?.userId));
    const internalCondition =
        user.isUserInternal === true && enableInternalUser === true;

    const externalCondition =
        user.isUserInternal === false && enableExternalUser === true;

    // If ANY of the conditions is true → return subViewNames
    if (isUserMatched || internalCondition || externalCondition) {
        return subViewNames;
    }

    return [];
}

export const getHighVolumeReport = ({
    enableExternalUser,
    enableInternalUser,
    isfUserIds,
    user,
}) => {
    const isUserMatched = isfUserIds
        .map(toLowerCase)
        .includes(toLowerCase(user?.userId));
    const internalCondition =
        user.isUserInternal === true && enableInternalUser === true;

    const externalCondition =
        user.isUserInternal === false && enableExternalUser === true;

    return isUserMatched || internalCondition || externalCondition;
};

export function filterSelectedReport(
    reportDetailsData,
    subViewNames,
    filterResult
) {
    if (filterResult.length > 0) {
        return reportDetailsData;
    }

    return reportDetailsData?.filter(
        (report) => !subViewNames?.includes(report?.SubViewName)
    );
}

// The data-views API returns Documents views for every user, so they must be
// dropped client-side wherever saved/recent views are listed.
export function filterDocumentDataViews(
    reportDetailsData,
    isDocumentLibraryEnabled
) {
    if (isDocumentLibraryEnabled) {
        return reportDetailsData;
    }

    return reportDetailsData?.filter(
        (report) => report?.DataViewType !== DATA_VIEW_TYPES.DOCUMENTS
    );
}

export function filterChangesDataViews(
    reportDetailsData,
    isOldChangesEnabled,
    isNewLeaveChangesEnabled
) {
    return reportDetailsData?.filter((report) => {
        if (
            report?.SubViewName === LEAVE_CHANGES_SUBVIEW &&
            report?.DataViewType === leaveType
        ) {
            return isNewLeaveChangesEnabled;
        }
        if (report?.DataViewType === changesReportType) {
            return isOldChangesEnabled;
        }
        return true;
    });
}

const isLeaveCoverageOption = (option) =>
    [option?.value, option?.label, option?.name].some(
        (value) => typeof value === "string" && value.toLowerCase() === "leave"
    );

// The data-views API returns every Coverage type option, so Leave has to be
// dropped client-side when the flag denies the user.
export function filterLeaveCoverageTypeOption(
    parametersOptions,
    isLeaveCoverageTypeEnabled
) {
    if (isLeaveCoverageTypeEnabled) {
        return parametersOptions;
    }

    return parametersOptions?.map((parameter) =>
        parameter?.parameterName === CONVERGE_TYPE
            ? {
                  ...parameter,
                  options: (parameter.options ?? []).filter(
                      (option) => !isLeaveCoverageOption(option)
                  ),
              }
            : parameter
    );
}

//navigation helpers

export const getEmployeeIdByClaimId = (claimId, info) => {
    const data = info.table.options.data;

    return data
        .filter((item) => item.claimId === claimId)
        .map((item) => item.employeeId)
        .filter((id) => id);
};

export const getEmployeeIdByLeaveId = (leaveId, info) => {
    const data = info.table.options.data;
    return data
        .filter((item) => item.leaveId === leaveId)
        .map((item) => item.employeeId)
        .filter((id) => id);
};

// Toast notification status helpers
export const TOAST_STATUS = {
    SUCCESS: "success",
    FAILED: "failed",
    PROGRESS: "progress",
};

const toastStatusConfig = {
    success: {
        text: "Done",
        className: "status-success",
        icon: "success-circle.svg",
    },
    failed: {
        text: "Failed",
        className: "status-failed",
        icon: "failed-circle.svg",
    },
    progress: {
        text: "In progress",
        className: "status-progress",
        icon: "progress-circle.svg",
    },
};

export const getToastStatusConfig = (status) => {
    return toastStatusConfig[status] || toastStatusConfig.progress;
};

export const getToastDefaultMessage = (files = []) => {
    const hasStatus = (s) => files.some((file) => file.status === s);
    const isDownload = files.some((file) => file.type === "download");

    if (hasStatus("failed")) {
        return isDownload
            ? "We couldn't download your document. Please try again later."
            : "We couldn't generate your report. Please try again later.";
    }
    if (hasStatus("progress")) {
        return isDownload
            ? "Downloading your document. It’ll be available in your browser’s downloads folder shortly."
            : "We're generating your report in the background. We’ll notify you when it’s ready to download. In the meantime, feel free to keep exploring Unum Insights";
    }
    if (files.every((file) => file.status === "success")) {
        return isDownload
            ? "Your document has been downloaded and is now available in your browser’s downloads folder."
            : "Your report has been generated and is now available in your browser’s downloads folder.";
    }
    return "Your report is being prepared.";
};

export const triggerDownload = (url, fileName) => {
    if (!url) return;
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "";
    link.style.display = "none";
    document.body.appendChild(link);

    // Prevent click event bubbling to document-level listeners
    link.addEventListener("click", (e) => {
        e.stopPropagation();
    });

    link.click();
    document.body.removeChild(link);
};
